import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Shield } from 'lucide-react'
import { fetchAdminRoles } from '../../api'
import { RoleDetailDialog } from '../dialogs/RoleDetailDialog'
import type { AdminRole } from '../../types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

function RoleCard({ role, onView }: { role: AdminRole; onView: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Shield className="size-4 shrink-0 text-muted-foreground" />
          <span className="font-semibold">
            {role.is_system
              ? t(`admin.roles.names.${role.slug}` as const, { defaultValue: role.name })
              : role.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {role.is_system ? (
            <Badge variant="secondary" className="text-xs">
              {t('admin.roles.systemBadge')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              {t('admin.roles.customBadge')}
            </Badge>
          )}
        </div>
      </div>

      {role.description && (
        <p className="text-sm text-muted-foreground">{role.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>{t('admin.roles.permissionCount', { count: role.permission_count })}</span>
        <span>·</span>
        <span>{t('admin.roles.userCount', { count: role.user_count })}</span>
      </div>

      <div className="flex flex-wrap gap-1">
        {role.permissions.slice(0, 6).map((p) => (
          <Badge key={p} variant="outline" className="rounded-full px-2 py-0 text-xs font-normal">
            {p}
          </Badge>
        ))}
        {role.permissions.length > 6 && (
          <Badge variant="outline" className="rounded-full px-2 py-0 text-xs font-normal text-muted-foreground">
            +{role.permissions.length - 6}
          </Badge>
        )}
      </div>

      <div className="flex items-center pt-1">
        <Button size="sm" variant="outline" onClick={onView}>
          {t('admin.roles.viewPermissions')}
        </Button>
      </div>
    </div>
  )
}

export function RolesTab() {
  const { t } = useTranslation()
  const [detailRole, setDetailRole] = useState<AdminRole | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: fetchAdminRoles,
  })

  const roles = rolesQuery.data?.data ?? []
  const systemRoles = roles.filter((r) => r.is_system)
  const customRoles = roles.filter((r) => !r.is_system)

  function openDetail(role: AdminRole) {
    setDetailRole(role)
    setDetailOpen(true)
  }

  return (
    <>
      <div className="space-y-8">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            {t('admin.roles.systemRolesTitle')}
          </h3>
          {rolesQuery.isPending ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-48 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {systemRoles.map((role) => (
                <RoleCard
                  key={role.id}
                  role={role}
                  onView={() => openDetail(role)}
                />
              ))}
            </div>
          )}
        </div>

        {customRoles.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {t('admin.roles.customRolesTitle')}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {customRoles.map((role) => (
                <RoleCard
                  key={role.id}
                  role={role}
                  onView={() => openDetail(role)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <RoleDetailDialog
        role={detailRole}
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open)
          if (!open) setDetailRole(null)
        }}
      />
    </>
  )
}
